import { cyan, deepPurple, grey, yellow } from "@mui/material/colors";
import { RcraInfoFederalWasteGeneratorStatus } from "generated-graphql/graphql";

export type ColorSettings = {
  backgroundColor: string;
  borderColor: string;
  chipColor?: string;
  textColor: string;
};
export enum GeneratorStatusKind {
  REPORTED = "reported",
  CALCULATED = "calculated",
}

/**
 * Map of federal generator status to color settings
 */
const federalStatusMap: Record<
  string,
  Record<GeneratorStatusKind, ColorSettings>
> = {
  [RcraInfoFederalWasteGeneratorStatus.LargeQuantityGenerator]: {
    [GeneratorStatusKind.REPORTED]: {
      backgroundColor: cyan[100],
      borderColor: cyan[400],
      textColor: cyan[900],
    },
    [GeneratorStatusKind.CALCULATED]: {
      backgroundColor: cyan[50],
      borderColor: cyan[400],
      textColor: cyan[900],
    },
  },
  [RcraInfoFederalWasteGeneratorStatus.SmallQuantityGenerator]: {
    [GeneratorStatusKind.REPORTED]: {
      chipColor: yellow[400],
      backgroundColor: yellow[200],
      borderColor: yellow[600],
      textColor: yellow[900],
    },
    [GeneratorStatusKind.CALCULATED]: {
      backgroundColor: yellow[50],
      borderColor: yellow[600],
      textColor: yellow[900],
    },
  },
  [RcraInfoFederalWasteGeneratorStatus.VerySmallQuantityGenerator]: {
    [GeneratorStatusKind.REPORTED]: {
      backgroundColor: deepPurple[100],
      borderColor: deepPurple[300],
      textColor: deepPurple[900],
    },
    [GeneratorStatusKind.CALCULATED]: {
      backgroundColor: deepPurple[50],
      borderColor: deepPurple[300],
      textColor: deepPurple[900],
    },
  },
  [RcraInfoFederalWasteGeneratorStatus.NotAGenerator]: {
    [GeneratorStatusKind.REPORTED]: {
      backgroundColor: grey[200],
      borderColor: grey[400],
      textColor: grey[800],
      chipColor: "transparent",
    },
    [GeneratorStatusKind.CALCULATED]: {
      backgroundColor: grey[100],
      borderColor: grey[400],
      textColor: grey[700],
      chipColor: "transparent",
    },
  },
  [RcraInfoFederalWasteGeneratorStatus.Pending]: {
    [GeneratorStatusKind.REPORTED]: {
      backgroundColor: grey[200],
      borderColor: grey[400],
      textColor: grey[800],
      chipColor: "transparent",
    },
    [GeneratorStatusKind.CALCULATED]: {
      backgroundColor: grey[100],
      borderColor: grey[400],
      textColor: grey[700],
      chipColor: "transparent",
    },
  },
  [RcraInfoFederalWasteGeneratorStatus.Undetermined]: {
    [GeneratorStatusKind.REPORTED]: {
      backgroundColor: grey[200],
      borderColor: grey[400],
      textColor: grey[800],
      chipColor: "transparent",
    },
    [GeneratorStatusKind.CALCULATED]: {
      backgroundColor: grey[100],
      borderColor: grey[400],
      textColor: grey[700],
      chipColor: "transparent",
    },
  },
  default: {
    [GeneratorStatusKind.REPORTED]: {
      backgroundColor: "transparent",
      borderColor: grey[400],
      textColor: "transparent",
      chipColor: "transparent",
    },
    [GeneratorStatusKind.CALCULATED]: {
      backgroundColor: "transparent",
      borderColor: grey[400],
      textColor: "transparent",
      chipColor: "transparent",
    },
  },
} as const;

/**
 * Get the color settings for a federal generator status.
 * This can be used for showing the chip or the classification bars.
 */
export const getFederalGeneratorStatusColorSettings = (
  status: RcraInfoFederalWasteGeneratorStatus | null,
  statusKind: GeneratorStatusKind
): ColorSettings => {
  if (!status || !(status in federalStatusMap)) {
    return federalStatusMap.default[statusKind];
  }
  const statusMap = federalStatusMap[status as keyof typeof federalStatusMap];
  const colorSettings = statusMap[statusKind];
  if (!colorSettings) {
    return federalStatusMap.default[statusKind];
  }
  return {
    ...colorSettings,
    chipColor: colorSettings.chipColor ?? colorSettings.backgroundColor,
  };
};
